import React, { useState, useEffect, useCallback } from 'react';
import { Card, Button, Table, Spinner } from 'react-bootstrap';
import { Plus } from 'lucide-react';
import { useAuth } from '../../../contexts/AuthContext';
import { API_ENDPOINTS } from '../../../config/api.config';
import { apiService } from '../../../services/apiService';
import CreateOrgModal from '../../../components/modals/organization/CreateOrgModal';
import ManageOrgModal from '../../../components/modals/organization/ManageOrgModal';
import PageTransition from '../../../components/layout/PageTransition';

interface Organization {
    id: string;
    name: string;
    domain: string;
    status: string;
    createdAt: string;
    user_count: string | number;
}

interface StandardResponse<T> {
    data: T;
    metadata?: {
        total?: number;
        page?: number;
        limit?: number;
    };
}

const transformResponse = <T,>(response: T[] | { data: T[] }): StandardResponse<T[]> => {
    if (Array.isArray(response)) {
        return {
            data: response,
            metadata: {
                total: response.length,
                page: 1,
                limit: response.length
            }
        };
    }

    if ('data' in response) {
        return {
            data: response.data,
            metadata: {
                total: response.data.length,
                page: 1,
                limit: response.data.length
            }
        };
    }

    throw new Error('Invalid response format');
};

const OrganizationsPage: React.FC = () => {
    const { user: authUser, isLoading: isAuthLoading, isAuthenticated } = useAuth();
    const [organizations, setOrganizations] = useState<Organization[]>([]);
    const [loading, setLoading] = useState(false);
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [showManageModal, setShowManageModal] = useState(false);
    const [selectedOrgId, setSelectedOrgId] = useState<string>('');
    const [error, setError] = useState('');
    const [isInitialized, setIsInitialized] = useState(false);

    const fetchOrganizations = useCallback(async () => {
        // Skip if we're still loading auth or don't have proper auth
        if (isAuthLoading || !isAuthenticated || !authUser?.role || authUser.role !== 'system_admin') {
            return;
        }

        try {
            setLoading(true);
            setError('');
            const rawResponse = await apiService.get<Organization[]>(API_ENDPOINTS.ORGANIZATIONS.LIST);
            console.log('Raw API Response:', rawResponse);

            const standardResponse = transformResponse(rawResponse);
            console.log('Transformed Response:', standardResponse);

            setOrganizations(standardResponse.data);
        } catch (err) {
            console.error('Error fetching organizations:', err);
            setError(err instanceof Error ? err.message : 'Failed to load organizations');
            setOrganizations([]);
        } finally {
            setLoading(true);
            // Use setTimeout to ensure we have a visual loading state
            setTimeout(() => {
                setLoading(false);
                setIsInitialized(true);
            }, 100);
        }
    }, [isAuthLoading, isAuthenticated, authUser]);

    // Initial load effect
    useEffect(() => {
        if (!isInitialized && isAuthenticated && authUser && !isAuthLoading) {
            console.log('Initializing organizations page:', {
                isInitialized,
                isAuthenticated,
                userRole: authUser?.role,
                isAuthLoading
            });
            fetchOrganizations();
        }
    }, [isInitialized, isAuthenticated, authUser, isAuthLoading, fetchOrganizations]);

    // Refresh effect for modal actions
    useEffect(() => {
        if (isInitialized && isAuthenticated && !isAuthLoading) {
            fetchOrganizations();
        }
    }, [showCreateModal, showManageModal]);

    const handleCreateSuccess = () => {
        setShowCreateModal(false);
        setIsInitialized(false); // Force a refresh
    };

    const handleManageClick = (orgId: string) => {
        setSelectedOrgId(orgId);
        setShowManageModal(true);
    };

    const handleManageSuccess = () => {
        setShowManageModal(false);
        setIsInitialized(false); // Force a refresh
    };

    const formatDate = (dateString: string) => {
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        });
    };

    // Show loading state during auth check or initial load
    if (isAuthLoading || (loading && !isInitialized)) {
        return (
            <div className="d-flex justify-content-center align-items-center p-4">
                <Spinner animation="border" role="status">
                    <span className="visually-hidden">Loading...</span>
                </Spinner>
            </div>
        );
    }

    // Show unauthorized message if not system admin
    if (!isAuthenticated || !authUser || authUser.role !== 'system_admin') {
        return (
            <div className="p-4">
                <div className="alert alert-danger">
                    You do not have permission to view this page.
                </div>
            </div>
        );
    }

    return (
        <div className="page-container">
            <PageTransition>
                <div className="p-4">
                    <div className="content-header d-flex justify-content-between align-items-center mb-4">
                        <div>
                            <h2 className="mb-1">Organizations</h2>
                            <p className="text-muted mb-0">Manage your organizations</p>
                        </div>
                        <Button
                            variant="primary"
                            onClick={() => setShowCreateModal(true)}
                            className="d-flex align-items-center gap-2"
                        >
                            <Plus size={20} />
                            New Organization
                        </Button>
                    </div>

                    <div className="content-card">
                        <Card>
                            <Card.Body>
                                {error && (
                                    <div className="alert alert-danger mb-4">
                                        {error}
                                    </div>
                                )}

                                {loading ? (
                                    <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
                                        <Spinner animation="border" role="status">
                                            <span className="visually-hidden">Loading organizations...</span>
                                        </Spinner>
                                    </div>
                                ) : (
                                    <Table responsive hover>
                                        <thead>
                                        <tr>
                                            <th>Name</th>
                                            <th>Domain</th>
                                            <th>Status</th>
                                            <th>Users</th>
                                            <th>Created</th>
                                            <th className="text-end">Actions</th>
                                        </tr>
                                        </thead>
                                        <tbody>
                                        {organizations.map((org) => (
                                            <tr key={org.id}>
                                                <td className="fw-medium">{org.name}</td>
                                                <td>{org.domain}</td>
                                                <td>
                                                        <span className={`badge bg-${org.status === 'active' ? 'success' : 'warning'}`}>
                                                            {org.status}
                                                        </span>
                                                </td>
                                                <td>{org.user_count} users</td>
                                                <td>{formatDate(org.createdAt)}</td>
                                                <td className="text-end">
                                                    <Button
                                                        variant="outline-primary"
                                                        size="sm"
                                                        onClick={() => handleManageClick(org.id)}
                                                    >
                                                        Manage
                                                    </Button>
                                                </td>
                                            </tr>
                                        ))}
                                        {organizations.length === 0 && !loading && (
                                            <tr>
                                                <td colSpan={6} className="text-center py-4">
                                                    <p className="text-muted mb-2">No organizations found</p>
                                                    <Button
                                                        variant="primary"
                                                        size="sm"
                                                        onClick={() => setShowCreateModal(true)}
                                                    >
                                                        Create your first organization
                                                    </Button>
                                                </td>
                                            </tr>
                                        )}
                                        </tbody>
                                    </Table>
                                )}
                            </Card.Body>
                        </Card>
                    </div>
                </div>
            </PageTransition>

            <CreateOrgModal
                show={showCreateModal}
                onHide={() => setShowCreateModal(false)}
                onSuccess={handleCreateSuccess}
            />

            <ManageOrgModal
                show={showManageModal}
                onHide={() => setShowManageModal(false)}
                orgId={selectedOrgId}
                onSuccess={handleManageSuccess}
            />
        </div>
    );
};

export default OrganizationsPage;